import { API_ERROR_NAMES, type ApiErrorNames } from "@coffitivity/shared";

/**
 * Process-wide signal that this client bundle is behind the server contract.
 *
 * Two independent triggers converge here into one state: the deployed-version
 * poll (proactive) and the apiClient's `CLIENT_OUTDATED` interceptor (reactive,
 * on an actual rejection). The UI subscribes and shows a single refresh prompt.
 * The flag latches — the only cure for a stale tab is a reload.
 */
let outdated = false;
const listeners = new Set<() => void>();

export function markClientOutdated(): void {
	if (outdated) return;
	outdated = true;
	for (const listener of listeners) listener();
}

export function isClientOutdated(): boolean {
	return outdated;
}

export function subscribeClientOutdated(listener: () => void): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/**
 * True when a request was rejected because this bundle is out of date. Duck-typed
 * on `code` so it holds regardless of the error's class or module identity.
 * Callers in a critical, mid-action flow (e.g. checkout) can use this to force a
 * reload rather than surfacing a dead-end error.
 */
export const isClientOutdatedError = (error: unknown): boolean =>
	(error as { code?: ApiErrorNames } | null)?.code ===
	API_ERROR_NAMES.CLIENT_OUTDATED;
